import React, { useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { firstAccess, requestPasswordReset, resetPassword } from '../api/user';
import { createAtivos } from '../api/ativos';
import './Login.css';

const API_URL = '/api/auth';

const FEATURES = [
  { icon: '🔍', text: 'Scanner B3 com Z-Score setorial em tempo real' },
  { icon: '📐', text: 'Kairi Ritsu — confluência de 5 indicadores' },
  { icon: '⚡', text: 'RSI(2) Connors com ~75% de win rate' },
  { icon: '🏛', text: 'Análise sistêmica vs. idiossincrática por setor' },
];

const TICKER_ITEMS = [
  { t: 'PETR4', c: '+1.24%', up: true },
  { t: 'VALE3', c: '-0.87%', up: false },
  { t: 'ITUB4', c: '+0.54%', up: true },
  { t: 'BBAS3', c: '+2.11%', up: true },
  { t: 'ABEV3', c: '-0.22%', up: false },
  { t: 'WEGE3', c: '+0.88%', up: true },
  { t: 'GGBR4', c: '-0.43%', up: false },
  { t: 'SUZB3', c: '+0.77%', up: true },
];

function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

const Login = ({ onLogin, onBack, resetToken = '', onResetComplete, initialMessage = '' }) => {
  const [step, setStep]         = useState(resetToken ? 'resetPassword' : 'login'); // 'login' | 'changePassword' | 'forgotPassword' | 'resetPassword'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // change-password step state
  const [pendingToken, setPendingToken]   = useState('');
  const [pendingEmail, setPendingEmail]   = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);
  const [newPass, setNewPass]     = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showNew, setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passError, setPassError] = useState('');
  const [passLoading, setPassLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(initialMessage);
  const [activeResetToken] = useState(resetToken);

  const createInitialAssets = async (userId) => {
    const assets = [
      { symbol: 'BBAS3', bolsa: 'STOCK', idUser: userId, listId: 0 },
      { symbol: 'PETR4', bolsa: 'STOCK', idUser: userId, listId: 0 },
      { symbol: 'USIM5', bolsa: 'STOCK', idUser: userId, listId: 0 },
      { symbol: 'VALE3', bolsa: 'STOCK', idUser: userId, listId: 0 },
      { symbol: 'EMBR3', bolsa: 'STOCK', idUser: userId, listId: 0 },
      { symbol: 'ITSA3', bolsa: 'STOCK', idUser: userId, listId: 0 },
      { symbol: 'BBDC4', bolsa: 'STOCK', idUser: userId, listId: 0 },
      { symbol: 'SUZB3', bolsa: 'STOCK', idUser: userId, listId: 0 },
    ];
    try {
      for (const asset of assets) await createAtivos(asset);
    } catch (err) {
      console.error('Failed to create initial assets:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg('');
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Usuário ou senha inválidos. Verifique e tente novamente.');
      }
      const data = await response.json();
      const decoded = jwtDecode(data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('userId', decoded.id);
      localStorage.setItem('userEmail', decoded.email || '');
      localStorage.setItem('username', decoded.username || username);

      if (decoded.firstAccess) {
        setPendingToken(data.token);
        setPendingEmail(decoded.email || '');
        setPendingUserId(decoded.id);
        setStep('changePassword');
        setIsLoading(false);
        return;
      }
      onLogin(data.token);
    } catch (error) {
      setErrorMsg(error.message || 'Falha ao autenticar. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPassError('');
    if (newPass.length < 6) { setPassError('A senha deve ter pelo menos 6 caracteres.'); return; }
    if (newPass !== confirmPass) { setPassError('As senhas não coincidem.'); return; }
    setPassLoading(true);
    try {
      await firstAccess(pendingEmail, newPass, pendingToken);
      await createInitialAssets(pendingUserId);
      onLogin(pendingToken);
    } catch (err) {
      setPassError(err?.response?.data?.message || err.message || 'Erro ao alterar senha.');
    } finally {
      setPassLoading(false);
    }
  };

  const goToLogin = () => {
    setStep('login');
    setErrorMsg('');
    setPassError('');
    setResetError('');
    setForgotMessage('');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMessage('');
    setErrorMsg('');
    try {
      const data = await requestPasswordReset(forgotEmail);
      setForgotMessage(data.message);
    } catch (_) {
      setForgotMessage('Se existir uma conta com esse e-mail, enviaremos um link para redefinir sua senha.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    if (newPass.length < 6) { setResetError('A senha deve ter pelo menos 6 caracteres.'); return; }
    if (newPass !== confirmPass) { setResetError('As senhas não coincidem.'); return; }
    setResetLoading(true);
    try {
      await resetPassword(activeResetToken, newPass);
      setResetSuccess('Senha atualizada. Entre com sua nova senha para acessar a plataforma.');
      setNewPass('');
      setConfirmPass('');
      setStep('login');
      onResetComplete?.();
    } catch (err) {
      setResetError(err?.response?.data?.message || 'Este link é inválido ou expirou. Solicite um novo link de recuperação.');
    } finally {
      setResetLoading(false);
    }
  };

  if (step === 'changePassword') {
    return (
      <div className="lg-root">
        <div className="lg-left">
          <div className="lg-left-glow lg-left-glow-1" />
          <div className="lg-left-glow lg-left-glow-2" />
          <div className="lg-left-content">
            <div className="lg-brand">
              <img src="/fatcat-logo-full.png" alt="FatCat" className="lg-logo" />
            </div>
            <h1 className="lg-headline">Bem-vindo ao<br /><span className="lg-accent">FatCat Pro</span></h1>
            <p className="lg-sub">Sua conta foi criada com sucesso.<br />Defina uma senha pessoal para continuar.</p>
          </div>
        </div>

        <div className="lg-right">
          <div className="lg-form-wrap">
            <div className="lg-form-header">
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
              <h2 className="lg-form-title">Criar sua senha</h2>
              <p className="lg-form-desc">Este é seu primeiro acesso. Escolha uma senha pessoal para proteger sua conta.</p>
            </div>

            <form className="lg-form" onSubmit={handleChangePassword} noValidate>
              <div className="lg-field">
                <label className="lg-label">Nova senha</label>
                <div className="lg-input-wrap">
                  <input
                    className="lg-input lg-input--pass"
                    type={showNew ? 'text' : 'password'}
                    value={newPass}
                    onChange={e => setNewPass(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button type="button" className="lg-eye" onClick={() => setShowNew(p => !p)} tabIndex={-1}>
                    <EyeIcon open={showNew} />
                  </button>
                </div>
              </div>

              <div className="lg-field">
                <label className="lg-label">Confirmar senha</label>
                <div className="lg-input-wrap">
                  <input
                    className="lg-input lg-input--pass"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPass}
                    onChange={e => setConfirmPass(e.target.value)}
                    placeholder="Repita a nova senha"
                    required
                    autoComplete="new-password"
                  />
                  <button type="button" className="lg-eye" onClick={() => setShowConfirm(p => !p)} tabIndex={-1}>
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
              </div>

              {passError && <div className="lg-error">{passError}</div>}

              <button className="lg-btn" type="submit" disabled={passLoading}>
                {passLoading ? <span className="lg-spinner" /> : 'Confirmar e entrar →'}
              </button>
            </form>

            <div className="lg-footer-note" style={{ marginTop: 24 }}>
              Conta: <strong style={{ color: '#94a3b8' }}>{pendingEmail}</strong>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'forgotPassword' || step === 'resetPassword') {
    const isReset = step === 'resetPassword';
    return (
      <div className="lg-root">
        <div className="lg-left">
          <div className="lg-left-glow lg-left-glow-1" />
          <div className="lg-left-glow lg-left-glow-2" />
          <div className="lg-left-content">
            <div className="lg-brand">
              <img src="/fatcat-logo-full.png" alt="FatCat" className="lg-logo" />
            </div>
            <h1 className="lg-headline">Seu acesso<br /><span className="lg-accent">continua seguro</span></h1>
            <p className="lg-sub">Recupere sua senha com segurança<br />e volte à sua análise do mercado.</p>
          </div>
        </div>

        <div className="lg-right">
          <div className="lg-form-wrap">
            <div className="lg-form-header">
              <div style={{ fontSize: 32, marginBottom: 8 }}>{isReset ? '🔐' : '✉️'}</div>
              <h2 className="lg-form-title">{isReset ? 'Criar nova senha' : 'Recuperar acesso'}</h2>
              <p className="lg-form-desc">
                {isReset
                  ? 'Escolha uma nova senha pessoal para proteger sua conta.'
                  : 'Informe o e-mail cadastrado e enviaremos as instruções.'}
              </p>
            </div>

            <form className="lg-form" onSubmit={isReset ? handleResetPassword : handleForgotPassword} noValidate>
              {isReset ? (
                <>
                  <div className="lg-field">
                    <label className="lg-label">Nova senha</label>
                    <div className="lg-input-wrap">
                      <input
                        className="lg-input lg-input--pass"
                        type={showNew ? 'text' : 'password'}
                        value={newPass}
                        onChange={e => setNewPass(e.target.value)}
                        placeholder="Mínimo 6 caracteres"
                        required
                        autoComplete="new-password"
                        autoFocus
                      />
                      <button type="button" className="lg-eye" onClick={() => setShowNew(p => !p)} tabIndex={-1}>
                        <EyeIcon open={showNew} />
                      </button>
                    </div>
                  </div>
                  <div className="lg-field">
                    <label className="lg-label">Confirmar senha</label>
                    <div className="lg-input-wrap">
                      <input
                        className="lg-input lg-input--pass"
                        type={showConfirm ? 'text' : 'password'}
                        value={confirmPass}
                        onChange={e => setConfirmPass(e.target.value)}
                        placeholder="Repita a nova senha"
                        required
                        autoComplete="new-password"
                      />
                      <button type="button" className="lg-eye" onClick={() => setShowConfirm(p => !p)} tabIndex={-1}>
                        <EyeIcon open={showConfirm} />
                      </button>
                    </div>
                  </div>
                  {resetError && <div className="lg-error">{resetError}</div>}
                  <button className="lg-btn" type="submit" disabled={resetLoading}>
                    {resetLoading ? <span className="lg-spinner" /> : 'Salvar nova senha →'}
                  </button>
                </>
              ) : (
                <>
                  <div className="lg-field">
                    <label className="lg-label">E-mail cadastrado</label>
                    <input
                      className="lg-input"
                      type="email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder="voce@email.com"
                      required
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                  {forgotMessage && <div className="lg-success">{forgotMessage}</div>}
                  <button className="lg-btn" type="submit" disabled={forgotLoading}>
                    {forgotLoading ? <span className="lg-spinner" /> : 'Enviar link de recuperação'}
                  </button>
                </>
              )}
            </form>

            {!isReset && (
              <p className="lg-security-note">
                Por segurança, a mensagem será a mesma mesmo que o e-mail não esteja cadastrado.
              </p>
            )}
            <button type="button" className="lg-text-button" onClick={goToLogin}>
              ← Voltar para o acesso
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg-root">

      {/* ── LEFT PANEL ─────────────────────────────────── */}
      <div className="lg-left">
        <div className="lg-left-glow lg-left-glow-1" />
        <div className="lg-left-glow lg-left-glow-2" />

        {onBack && (
          <button className="lg-back" onClick={onBack}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            Voltar
          </button>
        )}

        <div className="lg-left-content">
          <div className="lg-brand">
            <img src="/fatcat-logo-full.png" alt="FatCat" className="lg-logo" />
          </div>

          <h1 className="lg-headline">
            Opere a B3 com<br />
            <span className="lg-accent">precisão cirúrgica</span>
          </h1>
          <p className="lg-sub">
            Mean reversion sistemática. Sinais objetivos.<br />Backtest real de 252 pregões.
          </p>

          <ul className="lg-features">
            {FEATURES.map((f, i) => (
              <li key={i} className="lg-feature">
                <span className="lg-feature-icon">{f.icon}</span>
                <span>{f.text}</span>
              </li>
            ))}
          </ul>

          <div className="lg-stats">
            <div className="lg-stat">
              <span className="lg-stat-val">~78%</span>
              <span className="lg-stat-lbl">win rate</span>
            </div>
            <div className="lg-stat-div" />
            <div className="lg-stat">
              <span className="lg-stat-val">35</span>
              <span className="lg-stat-lbl">ativos B3</span>
            </div>
            <div className="lg-stat-div" />
            <div className="lg-stat">
              <span className="lg-stat-val">&gt;1.4</span>
              <span className="lg-stat-lbl">profit factor</span>
            </div>
          </div>
        </div>

        <div className="lg-tape-wrap">
          <div className="lg-tape-track">
            {[...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
              <span key={i} className="lg-tape-item">
                <span className="lg-tape-t">{item.t}</span>
                <span className={`lg-tape-c ${item.up ? 'up' : 'dn'}`}>{item.c}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ────────────────────────────────── */}
      <div className="lg-right">
        <div className="lg-form-wrap">

          <div className="lg-form-header">
            <h2 className="lg-form-title">Acesso à plataforma</h2>
            <p className="lg-form-desc">Entre com seu usuário e senha para continuar.</p>
          </div>

          <form className="lg-form" onSubmit={handleSubmit} noValidate>
            <div className="lg-field">
              <label className="lg-label">Usuário</label>
              <input
                className="lg-input"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="seu.usuario"
                required
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="lg-field">
              <label className="lg-label">Senha</label>
              <div className="lg-input-wrap">
                <input
                  className="lg-input lg-input--pass"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lg-eye"
                  onClick={() => setShowPass(p => !p)}
                  tabIndex={-1}
                >
                  <EyeIcon open={showPass} />
                </button>
              </div>
            </div>

            {errorMsg && <div className="lg-error">{errorMsg}</div>}
            {resetSuccess && <div className="lg-success">{resetSuccess}</div>}

            <button className="lg-btn" type="submit" disabled={isLoading}>
              {isLoading ? <span className="lg-spinner" /> : 'Entrar na plataforma'}
            </button>
          </form>

          <button type="button" className="lg-text-button lg-forgot-button" onClick={() => {
            setStep('forgotPassword');
            setForgotMessage('');
            setResetSuccess('');
          }}>
            Esqueci minha senha
          </button>

          <div className="lg-divider" />

          <p className="lg-hint-cta">
            Ainda não é assinante?{' '}
            <a
              className="lg-link"
              href="https://pay.kiwify.com.br/qGNnjXV"
              target="_blank"
              rel="noopener noreferrer"
            >
              Assinar agora →
            </a>
          </p>

          <div className="lg-footer-note">
            Plataforma de análise quantitativa · Não é recomendação de investimento
          </div>

          <div className="lg-admin-link-wrap">
            <a className="lg-admin-link" href="/#admin">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Área administrativa
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
